import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { DatePicker, Drawer, Select, Input, Tooltip, message } from 'antd';
import { InfoCircleOutlined, DownloadOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import moment from 'moment';
import { TripHistoryRCTable, ButtonWrapper, TripExportWarningModal } from 'components';
import { Box, Flex, Text } from 'atoms';
import { FilterTitle, FilterTag } from 'components/common';
import {
	getTripListAction,
	getTripListActionV2,
	getTripFilterList,
	getListAction,
	exportTripsCSVV2,
	checkTripCloseEligibility,
	bulkCloseTrips,
} from 'store/pageSlice';
import BulkCloseTripsModal from 'componentsv2/modals/BulkCloseTripsModal';
import { IntercityTripTableConfig, initialPaginateConfig, DATE_FORMAT_WITH_TIME } from 'utils';
import { exportDateGuard } from 'utils/exportDateGuard';
import { ACTIVE_TRIP_STATUS_OPTIONS, TRIP_STATUS_ID } from 'utils/tripStatus';
import { useQueryParams, useAuth, useCascadingTripFilters } from 'hooks';
import { FilterAtnButton } from 'utils/commonStyled';
import IntercityEmptyState, { EmptyStateText } from './IntercityEmptyState';
import EndTripModal from './EndTripModal';
import EndTripModalV2 from './EndTripModalV2';
import { Tabs } from 'atoms/Tabs';
import { getUIVersion } from 'utils/router-static-data';
import {
	loadTripFilters,
	saveTripFilters,
	clearTripFilters,
	formatDateForAPI,
	formatEndDateForAPI,
	formatDateForRangePicker,
} from 'utils/datePersistence';

const { Search } = Input;

const LtSearchBar = styled(Search)`
	border: none;
`;
const CloseSelectedButton = styled(ButtonWrapper)`
	color: #ff4747;
	border: 1px solid #ff4747;
	&:hover:not(:disabled) {
		color: #ff4747;
		background-color: rgba(255, 71, 71, 0.08);
	}
	/* The base Button sets opacity: 0.2 when disabled, which makes the label
	   unreadable. Override it so the button stays legible while nothing is selected. */
	&:disabled {
		opacity: 1;
		color: #777777;
		border-color: #d9d9d9;
		background-color: transparent;
		cursor: not-allowed;
	}
`;
const tripKey = (value) => String(value ?? '').replace(/^IRPL-/i, '');

const allOption = {
	value: '',
	label: 'All',
	key: 'All',
};

const TripStatusOptions = [allOption, ...ACTIVE_TRIP_STATUS_OPTIONS];

const validOptions = [
	{ value: 'true', label: 'Valid', key: true },
	{ value: 'false', label: 'Invalid', key: false },
];
validOptions.unshift(allOption);

function populateOptions(data = [], showAll = true) {
	/**
	 * assign id & value to option list
	 */
	data = data?.map((element) => ({
		value: element?.id,
		label: element?.value,
	}));

	/**
	 * Add All option of array first
	 */
	if (showAll) {
		data.unshift(allOption);
	}

	return data;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export default function IntercityTripListPage() {
	const dispatch = useDispatch();
	const location = useLocation();
	const { RangePicker } = DatePicker;
	const [citiesList, setCitiesList] = useState('');
	const [clientsList, setClientsList] = useState('');
	const [projectsList, setProjectsList] = useState('');
	const [vendorsList, setVendorsList] = useState('');
	const [businessUnitsList, setBusinessUnitsList] = useState([]);
	const [open, setOpen] = useState(false);
	const [rowData, setRowData] = useState({});
	const [paginateConfig, setpaginateConfig] = useQueryParams({
		search_type: 'trip',
		fetch_filters: false,
		business_unit_ids: [],
	});
	const [searchValue, setSearchValue] = useState('');
	useEffect(() => {
		setSearchValue(paginateConfig?.query_str || '');
	}, [paginateConfig?.query_str]);
	const { options: cascadeOptions } = useCascadingTripFilters({
		paginateConfig,
		enabled: getUIVersion() === 'new' && open,
		isEnded: false,
	});
	useEffect(() => {
		if (getUIVersion() !== 'new') return;
		setCitiesList([allOption, ...(cascadeOptions.cities || [])]);
		setClientsList([allOption, ...(cascadeOptions.clients || [])]);
		setProjectsList([allOption, ...(cascadeOptions.projects || [])]);
		setVendorsList([allOption, ...(cascadeOptions.vendors || [])]);
	}, [cascadeOptions]);
	const [showEndTripModal, setShowEndTripModal] = useState(false);
	const [selectedTrip, setSelectedTrip] = useState(null);
	const [activeTab, setActiveTab] = useState('all');
	const [rangePickerValue, setRangePickerValue] = useState(null);
	const [exportWarningModal, setExportWarningModal] = useState(false);
	const tableRefreshRef = useRef();

	const [selectedTrips, setSelectedTrips] = useState([]);
	const [eligibilityMap, setEligibilityMap] = useState({});
	const [eligibilityLoading, setEligibilityLoading] = useState(false);
	const [showBulkCloseConfirm, setShowBulkCloseConfirm] = useState(false);
	const [bulkCloseLoading, setBulkCloseLoading] = useState(false);
	const [bulkCloseResult, setBulkCloseResult] = useState(null);
	// Force a one-time reload when arriving on this page to avoid stale state
	useEffect(() => {
		const reloadKey = `reload_once:${window.location.pathname}${window.location.search}`;
		if (!sessionStorage.getItem(reloadKey)) {
			sessionStorage.setItem(reloadKey, '1');
			window.location.replace(`${window.location.pathname}${window.location.search}`);
		}
	}, []);
	const { permissions, user_type: userType } = useAuth();
	const canEndTrip =
		permissions?.update?.includes('trip') || permissions?.update?.includes('all');

	const isAdminUser = userType === 'admin';
	const showBulkCloseUI = getUIVersion() === 'new' && isAdminUser;

	// Selection persists across pagination (an admin closing many "Trip Ended"
	// trips will often page through more than fit on one screen) - merge against
	// the current page's rows rather than replacing wholesale.
	const handleBulkSelectionChange = useCallback(
		(keys) => {
			setSelectedTrips((prev) => {
				const keySet = new Set(keys);
				const kept = prev.filter((t) => keySet.has(t.id));
				const keptIds = new Set(kept.map((t) => t.id));
				const currentPageRows = rowData?.rows || [];
				const added = currentPageRows.filter((t) => keySet.has(t.id) && !keptIds.has(t.id));
				return [...kept, ...added];
			});
		},
		[rowData?.rows]
	);

	// A stale selection/eligibility batch must not survive a filter change.
	useEffect(() => {
		setSelectedTrips([]);
		setEligibilityMap({});
	}, [paginateConfig?.trip_status, paginateConfig?.client_id, paginateConfig?.business_unit_ids]);

	const endedRows = useMemo(
		() =>
			(rowData?.rows || []).filter((t) => Number(t.trip_status_id) === TRIP_STATUS_ID.ENDED),
		[rowData?.rows]
	);

	const visibleClientIds = useMemo(() => {
		if (paginateConfig?.client_id) {
			return [paginateConfig.client_id];
		}
		return Array.from(new Set(endedRows.map((t) => t.client_id).filter(Boolean)));
	}, [paginateConfig?.client_id, endedRows]);

	// Re-check eligibility whenever the "Trip Ended" list refreshes.
	useEffect(() => {
		if (!showBulkCloseUI || !endedRows.length || !visibleClientIds.length) {
			return;
		}
		setEligibilityLoading(true);
		Promise.all(
			visibleClientIds.map((clientId) =>
				dispatch(
					checkTripCloseEligibility({
						client_id: clientId,
						trip_status_id: 9,
						trip_category: 'intercity',
					})
				)
			)
		)
			.then((results) => {
				const map = {};
				results.forEach((res) => {
					(res?.eligible || []).forEach((e) => {
						map[tripKey(e.trip_id)] = { eligible: true };
					});
					(res?.not_eligible || []).forEach((e) => {
						map[tripKey(e.trip_id)] = { eligible: false, reason: e.reason };
					});
				});
				setEligibilityMap(map);
			})
			.finally(() => setEligibilityLoading(false));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [showBulkCloseUI, endedRows, visibleClientIds, dispatch]);

	const handleConfirmBulkClose = () => {
		setBulkCloseLoading(true);
		dispatch(bulkCloseTrips(selectedTrips.map((t) => t.id)))
			.then((res) => {
				setBulkCloseResult(
					res?.data || { closed: [], failed: [], total_count: 0, success_count: 0 }
				);
				if (res?.data?.failed_count > 0) {
					message.warning(
						`${res.data.success_count} of ${res.data.total_count} trips closed, ${res.data.failed_count} failed.`
					);
				} else {
					message.success(`All ${res?.data?.total_count || 0} trip(s) closed.`);
				}
			})
			.catch((error) => {
				console.error('Bulk close trips error:', error);
				message.error(error?.message || 'Failed to close trips');
			})
			.finally(() => setBulkCloseLoading(false));
	};

	const handleDoneBulkClose = () => {
		setShowBulkCloseConfirm(false);
		setBulkCloseResult(null);
		setSelectedTrips([]);
		setEligibilityMap({});
		// Refetch so the trips that just moved to Trip Closed drop out of the Trip Ended
		// list. Done by giving paginateConfig a new identity rather than by calling
		// tableRefreshRef: the table owns the fetch that writes rowData, and its effect
		// keys off paginateConfig, so this is the one path guaranteed to match the rows
		// on screen. refreshTable builds its own params and discards its response.
		setpaginateConfig((prev) => ({ ...prev, offset: 0, currentPage: 1 }));
	};

	const memoizedDispatchAction = useCallback(
		(params) => {
			const isV2UI = getUIVersion() === 'new';
			const tabParams = {
				trip_category: 'intercity',
				...params,
				fetch_filters: false,
				business_unit_ids: getBusinessUnitIds(),
			};
			if (activeTab === 'long') {
				tabParams.duration = 'long_trips';
			}

			// Use v2 API for new UI, v1 for old UI
			if (isV2UI) {
				return getTripListActionV2(tabParams);
			}
			return getTripListAction(tabParams);
		},
		[activeTab, paginateConfig?.business_unit_ids]
	);

	// Create refresh function and assign to ref
	const refreshTable = useCallback(() => {
		const isV2UI = getUIVersion() === 'new';
		const refreshParams = {
			...paginateConfig,
			trip_category: 'intercity',
			fetch_filters: false,
			business_unit_ids: getBusinessUnitIds(),
		};

		// Use v2 API for new UI, v1 for old UI
		if (isV2UI) {
			// For v2 API, trip_type should be in filter_by, not at top level
			if (refreshParams.trip_category === 'intercity') {
				refreshParams.trip_type = 'intercity';
			}
			return dispatch(getTripListActionV2(refreshParams)).catch((error) => {
				console.error('Refresh trips V2 error:', error);
				message.error('Failed to refresh trip list');
			});
		}
		return dispatch(getTripListAction(refreshParams)).catch((error) => {
			console.error('Refresh trips error:', error);
			message.error('Failed to refresh trip list');
		});
	}, [dispatch, paginateConfig, paginateConfig?.business_unit_ids]);

	// Assign refresh function to ref
	React.useEffect(() => {
		tableRefreshRef.current = refreshTable;
	}, [refreshTable]);

	const buildExportParams = useCallback(() => {
		const params = {
			...paginateConfig,
			trip_category: 'intercity',
			fetch_filters: false,
			business_unit_ids: getBusinessUnitIds(),
		};
		if (activeTab === 'long') {
			params.duration = 'long_trips';
		}
		return params;
	}, [paginateConfig, activeTab]);

	const runExport = useCallback(
		(fromDate, toDate) => {
			const params = { ...buildExportParams(), from_date: fromDate, to_date: toDate };
			return dispatch(exportTripsCSVV2(params)).catch((error) => {
				console.error('Export intercity trips error:', error);
				message.error('Failed to export trips');
			});
		},
		[dispatch, buildExportParams]
	);

	const handleExportClick = () => {
		const fromDate = paginateConfig?.from_date;
		const toDate = paginateConfig?.to_date;
		const { ok } = exportDateGuard(fromDate, toDate);
		if (!ok) {
			setExportWarningModal(true);
			return;
		}
		runExport(fromDate, toDate);
	};

	const handleExportWarningConfirm = () => {
		const toDate = moment(new Date()).format(DATE_FORMAT_WITH_TIME);
		const fromDate = moment(toDate).subtract(60, 'days').format(DATE_FORMAT_WITH_TIME);
		setExportWarningModal(false);
		runExport(fromDate, toDate);
	};

	// Helper: Set filter list based on filter type
	const setFilterList = useCallback((filter, options) => {
		switch (filter) {
			case 'client':
				setClientsList(options);
				break;
			case 'project':
				setProjectsList(options);
				break;
			case 'vendor':
				setVendorsList(options);
				break;
			default:
				break;
		}
	}, []);

	// Helper: Load city filter (always V1)
	const loadCityFilter = useCallback(() => {
		dispatch(getTripFilterList('city'))
			.then((res) => {
				const { rows = [] } = res;
				const cities = rows?.cities?.map((element) => ({
					value: element,
					label: element,
				}));
				cities.unshift(allOption);
				setCitiesList(cities);
			})
			.catch((error) => {
				console.error('Error fetching city filter:', error);
				setCitiesList([allOption]);
			});
	}, [dispatch]);

	// Helper: Load V1 filters (client, project, vendor)
	const loadV1Filter = useCallback(
		(filter) => {
			dispatch(getTripFilterList(filter))
				.then((res) => {
					const { rows = [] } = res;
					let options = [];

					if (filter === 'client') {
						options = populateOptions(rows.clients);
					} else if (filter === 'project') {
						options = populateOptions(rows.projects);
					} else if (filter === 'vendor') {
						options = populateOptions(rows.vendors);
					}

					setFilterList(filter, options);
				})
				.catch((error) => {
					console.error(`Error fetching ${filter} filter (V1):`, error);
					setFilterList(filter, [allOption]);
				});
		},
		[dispatch, setFilterList]
	);

	// Helper: Load business units (always V1)
	const loadBusinessUnits = useCallback(() => {
		dispatch(
			getListAction({
				search_type: 'business_unit',
				limit: 100,
				offset: 0,
				version: 'v1', // Explicitly use V1 API to prevent login expiration
			})
		)
			.then((res) => {
				const buOptions = (res?.rows || []).map((bu) => ({
					value: String(bu.id),
					label: bu.name,
				}));
				setBusinessUnitsList(buOptions);
			})
			.catch((error) => {
				console.error('Error fetching business unit filter:', error);
				setBusinessUnitsList([]);
			});
	}, [dispatch]);

	// Function to load filter data when filter drawer opens
	const loadFilterData = useCallback(() => {
		const isV2UI = getUIVersion() === 'new';

		if (isV2UI) {
			loadBusinessUnits();
			return;
		}

		loadCityFilter();
		['client', 'project', 'vendor'].forEach((filter) => loadV1Filter(filter));
		loadBusinessUnits();
	}, [loadCityFilter, loadV1Filter, loadBusinessUnits]);

	// Calculate business unit filter value when business units list or IDs change
	useEffect(() => {
		const buIds = getBusinessUnitIds();
		if (buIds.length === 0) {
			setBusinessUnitFilterValue('');
			return;
		}

		// Ensure businessUnitsList is an array before using find
		if (!Array.isArray(businessUnitsList) || businessUnitsList.length === 0) {
			setBusinessUnitFilterValue('Business Unit');
			return;
		}

		// Get the business unit names for display
		const buNames = buIds
			.map((id) => {
				const businessUnit = businessUnitsList.find((item) => item.value === id);
				if (!businessUnit) {
					console.log(
						'Business unit not found for ID:',
						id,
						'Available options:',
						businessUnitsList
					);
				}
				return businessUnit ? businessUnit.label : id;
			})
			.filter(Boolean);

		setBusinessUnitFilterValue(buNames.length > 0 ? 'Business Unit' : '');
	}, [businessUnitsList, paginateConfig?.business_unit_ids]);

	// Load persisted date range and filters on component mount
	useEffect(() => {
		const { dateRange, filters } = loadTripFilters('INTERCITY');

		// Load date range only if not already in URL
		if (
			dateRange?.from_date_moment &&
			dateRange?.to_date_moment &&
			!paginateConfig.from_date &&
			!paginateConfig.to_date
		) {
			const fromDate = formatDateForRangePicker(dateRange.from_date_moment);
			const toDate = formatDateForRangePicker(dateRange.to_date_moment);
			if (fromDate && toDate) {
				setRangePickerValue([fromDate, toDate]);

				// Also set the API parameters for the date range
				const apiFromDate = formatDateForAPI(fromDate);
				const apiToDate = formatEndDateForAPI(toDate);
				setpaginateConfig((prev) => ({
					...prev,
					from_date: apiFromDate,
					to_date: apiToDate,
				}));
			}
		}

		// Load other filters only if not already in URL
		if (filters) {
			const filtersToApply = {};
			Object.keys(filters).forEach((key) => {
				// Only apply filter if it's not already in URL params
				if (paginateConfig[key] === undefined) {
					filtersToApply[key] = filters[key];
				}
			});

			if (Object.keys(filtersToApply).length > 0) {
				setpaginateConfig((prev) => ({
					...prev,
					...filtersToApply,
				}));
			}
		}

		// Mark initial load as complete
		setIsInitialLoadComplete(true);
	}, []);

	// Sync RangePicker UI with URL/state-provided dates
	useEffect(() => {
		if (paginateConfig?.from_date && paginateConfig?.to_date) {
			const fromUtc = moment.utc(paginateConfig.from_date, 'MM/DD/YYYY HH:mm:ss');
			const toUtc = moment.utc(paginateConfig.to_date, 'MM/DD/YYYY HH:mm:ss');
			if (fromUtc.isValid() && toUtc.isValid()) {
				// Convert UTC payload times back to local (IST) for calendar display
				const fromLocal = fromUtc.add(330, 'minutes');
				const toLocal = toUtc.add(330, 'minutes');
				setRangePickerValue([fromLocal, toLocal]);
			}
		}
	}, [paginateConfig?.from_date, paginateConfig?.to_date]);

	// Save filters to localStorage whenever paginateConfig changes (excluding date changes)
	useEffect(() => {
		// Don't save on initial load
		if (Object.keys(paginateConfig).length > 0) {
			// Extract filters excluding date fields
			const { from_date, to_date, ...filters } = paginateConfig;

			// Only save if there are actual filters
			if (Object.keys(filters).length > 0) {
				saveTripFilters('INTERCITY', null, filters);
			}
		}
	}, [paginateConfig]);

	// Clear the refresh state on component mount to prevent unnecessary refetches
	useEffect(() => {
		if (location.state?.refresh) {
			// Clear the state so it doesn't refetch on every visit
			window.history.replaceState({}, document.title);
		}
	}, [location.state]);

	const StatusValue = paginateConfig?.is_active ? 'Status' : '';
	const UserVal = paginateConfig?.user_type ? 'User Type' : '';
	const cityFilterValue = paginateConfig?.city ? 'City' : '';

	// Helper function to handle business unit IDs (array vs string from URL)
	const getBusinessUnitIds = () => {
		const buIds = paginateConfig?.business_unit_ids;
		let result = [];

		if (Array.isArray(buIds)) {
			result = buIds;
		} else if (buIds) {
			result = [buIds];
		}

		return result.map((businessUnitId) => String(businessUnitId).trim()).filter(Boolean);
	};

	const [businessUnitFilterValue, setBusinessUnitFilterValue] = useState('');
	const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);

	const client = paginateConfig?.client_id ? 'Client' : '';
	const project = paginateConfig?.project_id ? 'Project' : '';
	const StatusValueAll = paginateConfig?.is_active === '' ? 'Status' : '';
	const UserAllVal = paginateConfig?.user_type === '' ? 'User Type' : '';
	const clientAll = paginateConfig?.client_id === '' ? 'Client' : '';
	const projectAll = paginateConfig?.project_id === '' ? 'Project' : '';
	const cityAllValue = paginateConfig?.city === '' ? 'City' : '';
	const vendorValue = paginateConfig?.vendor_id === '' ? 'Vendor' : '';
	const VendorAllValue = paginateConfig?.vendor_id ? 'Vendor' : '';
	const tripStatus = paginateConfig?.trip_status ? 'Status' : '';
	const tripAllStatus = paginateConfig?.trip_status === '' ? 'Status' : '';
	const tripType = paginateConfig?.trip_type ? 'Type' : '';
	const tripAllType = paginateConfig?.trip_type === '' ? 'Type' : '';
	const All_is_valid = paginateConfig?.is_valid === '' ? 'Valid' : '';
	const All_is_valid_fasle = paginateConfig?.is_valid === false ? 'Valid' : '';
	const is_valid = paginateConfig?.is_valid ? 'Valid' : '';

	function handleValidTripTypeChange(val) {
		const finalVal = val === '' && val !== undefined ? 'All' : val;
		setpaginateConfig((prev) => ({
			...prev,
			is_valid: finalVal,
			offset: 0,
			currentPage: 1,
		}));
	}

	// Individual filter clear functions
	const clearCityFilter = () => {
		setpaginateConfig((prev) => ({
			...prev,
			city: undefined,
			offset: 0,
			currentPage: 1,
		}));
	};

	const clearClientFilter = () => {
		setpaginateConfig((prev) => ({
			...prev,
			client_id: undefined,
			client_name: undefined,
			offset: 0,
			currentPage: 1,
		}));
	};

	const clearProjectFilter = () => {
		setpaginateConfig((prev) => ({
			...prev,
			project_id: undefined,
			project_location_code: undefined,
			offset: 0,
			currentPage: 1,
		}));
	};

	const clearVendorFilter = () => {
		setpaginateConfig((prev) => ({
			...prev,
			vendor_id: undefined,
			vendor_name: undefined,
			offset: 0,
			currentPage: 1,
		}));
	};

	const clearTripStatusFilter = () => {
		setpaginateConfig((prev) => ({
			...prev,
			trip_status: undefined,
			offset: 0,
			currentPage: 1,
		}));
	};

	const clearValidFilter = () => {
		setpaginateConfig((prev) => ({
			...prev,
			is_valid: undefined,
			offset: 0,
			currentPage: 1,
		}));
	};
	const clearBusinessUnitFilter = () => {
		setpaginateConfig((prev) => ({
			...prev,
			business_unit_ids: undefined,
			offset: 0,
			currentPage: 1,
		}));
	};

	return (
		<>
			<Box
				background="#FFFFFF"
				borderRadius="8px"
				p="24px"
				boxShadow="0 2px 8px rgba(0,0,0,0.04)"
				mb={4}
			>
				<Box mb={3} display="flex" alignItems="center" justifyContent="space-between">
					<Tabs
						activeKey={activeTab}
						onChange={setActiveTab}
						items={[
							{
								label: 'All Trips',
								key: 'all',
							},
							{
								label: 'Long trips',
								key: 'long',
							},
						]}
						tabBarStyle={{ borderBottom: 'none', marginBottom: 0 }}
					/>
					<Box flex="0 0 auto" display="flex" alignItems="center" ml="auto">
						<Tooltip title="Long Trip = > 48 Hours">
							<InfoCircleOutlined
								style={{ color: 'black', fontSize: 20, marginLeft: 2 }}
							/>
						</Tooltip>
					</Box>
				</Box>
				<Flex
					justifyContent="space-between"
					alignItems="center"
					mt={3}
					gap="16px"
					rowGap="12px"
					flexWrap="wrap"
				>
					<Box flex="1 1 160px" marginRight="8px" minWidth="120px" maxWidth="260px">
						<LtSearchBar
							placeholder="Search"
							enterButton="Search"
							value={searchValue}
							allowClear
							onChange={(e) => setSearchValue(e.target.value)}
							onSearch={(val) => {
								setSearchValue(val || '');
								const preservedFrom =
									paginateConfig?.from_date ||
									(rangePickerValue?.[0]
										? formatDateForAPI(rangePickerValue[0])
										: undefined);
								const preservedTo =
									paginateConfig?.to_date ||
									(rangePickerValue?.[1]
										? formatEndDateForAPI(rangePickerValue[1])
										: undefined);

								setpaginateConfig({
									...initialPaginateConfig,
									query_str: val,
									search_type: 'trip',
									fetch_filters: false,
									offset: 0,
									currentPage: 1,
									...(preservedFrom ? { from_date: preservedFrom } : {}),
									...(preservedTo ? { to_date: preservedTo } : {}),
								});
							}}
						/>
					</Box>
					<Box
						flex="0 0 auto"
						display="flex"
						alignItems="center"
						flexWrap="wrap"
						className="filter-highlight"
						gap="8px"
					>
						<Box
							width="120px"
							height="37px"
							padding="10px 10px 10px 12px"
							background="#FFFFFF"
							border="1px solid #C6C6C8"
							borderRadius="2px"
							display="flex"
							alignItems="center"
							overflowX="auto"
							whiteSpace="nowrap"
							gap="8px"
							className="filter-tags-scroll-container"
						>
							{StatusValue && (
								<FilterTag
									onClose={() =>
										setpaginateConfig((prev) => ({
											...prev,
											is_active: undefined,
											offset: 0,
											currentPage: 1,
										}))
									}
								>
									{StatusValue}
								</FilterTag>
							)}
							{StatusValueAll && (
								<FilterTag
									onClose={() =>
										setpaginateConfig((prev) => ({
											...prev,
											is_active: undefined,
											offset: 0,
											currentPage: 1,
										}))
									}
								>
									{StatusValueAll}
								</FilterTag>
							)}
							{projectAll && (
								<FilterTag
									onClose={() =>
										setpaginateConfig((prev) => ({
											...prev,
											project_id: undefined,
										}))
									}
								>
									{projectAll}
								</FilterTag>
							)}
							{UserVal && (
								<FilterTag
									onClose={() =>
										setpaginateConfig((prev) => ({
											...prev,
											user_id: undefined,
											offset: 0,
											currentPage: 1,
										}))
									}
								>
									{UserVal}
								</FilterTag>
							)}
							{cityFilterValue && (
								<FilterTag onClose={clearCityFilter}>{cityFilterValue}</FilterTag>
							)}
							{businessUnitFilterValue && (
								<FilterTag onClose={clearBusinessUnitFilter}>
									{businessUnitFilterValue}
								</FilterTag>
							)}
							{client && <FilterTag onClose={clearClientFilter}>{client}</FilterTag>}
							{project && (
								<FilterTag onClose={clearProjectFilter}>{project}</FilterTag>
							)}
							{UserAllVal && (
								<FilterTag
									onClose={() =>
										setpaginateConfig((prev) => ({
											...prev,
											user_id: undefined,
											offset: 0,
											currentPage: 1,
										}))
									}
								>
									{UserAllVal}
								</FilterTag>
							)}
							{clientAll && (
								<FilterTag onClose={clearClientFilter}>{clientAll}</FilterTag>
							)}
							{cityAllValue && (
								<FilterTag onClose={clearCityFilter}>{cityAllValue}</FilterTag>
							)}
							{VendorAllValue && (
								<FilterTag onClose={clearVendorFilter}>{VendorAllValue}</FilterTag>
							)}
							{vendorValue && (
								<FilterTag onClose={clearVendorFilter}>{vendorValue}</FilterTag>
							)}
							{tripStatus && (
								<FilterTag onClose={clearTripStatusFilter}>{tripStatus}</FilterTag>
							)}
							{tripAllStatus && (
								<FilterTag onClose={clearTripStatusFilter}>
									{tripAllStatus}
								</FilterTag>
							)}
							{tripType && (
								<FilterTag
									onClose={() =>
										setpaginateConfig((prev) => ({
											...prev,
											trip_type: undefined,
											offset: 0,
											currentPage: 1,
										}))
									}
								>
									{tripType}
								</FilterTag>
							)}
							{tripAllType && (
								<FilterTag
									onClose={() =>
										setpaginateConfig((prev) => ({
											...prev,
											trip_type: undefined,
											offset: 0,
											currentPage: 1,
										}))
									}
								>
									{tripAllType}
								</FilterTag>
							)}
							{All_is_valid && (
								<FilterTag onClose={clearValidFilter}>{All_is_valid}</FilterTag>
							)}
							{is_valid && (
								<FilterTag onClose={clearValidFilter}>{is_valid}</FilterTag>
							)}
							{All_is_valid_fasle && (
								<FilterTag onClose={clearValidFilter}>
									{All_is_valid_fasle}
								</FilterTag>
							)}
						</Box>
						<FilterAtnButton
							onClick={() => {
								setOpen(true);
								loadFilterData();
							}}
						>
							FILTERS
						</FilterAtnButton>
					</Box>
					<Box
						flex="0 0 auto"
						display="flex"
						alignItems="center"
						gap="6px"
						minWidth="0"
						marginLeft="8px"
					>
						<Text whiteSpace="nowrap" marginRight="1">
							Select Date :
						</Text>
						<RangePicker
							name="date_filter"
							style={{ width: '210px' }}
							allowClear
							value={rangePickerValue}
							format={['DD MMM YY', 'YYYY-MM-DD']}
							onChange={(val) => {
								setRangePickerValue(val);
								if (val) {
									const fromDate = formatDateForAPI(val[0]);
									const toDate = formatEndDateForAPI(val[1]);
									const newConfig = {
										...paginateConfig,
										from_date: fromDate,
										to_date: toDate,
										offset: 0,
										currentPage: 1,
									};
									setpaginateConfig(newConfig);

									// Save both dates and filters to localStorage
									saveTripFilters(
										'INTERCITY',
										{
											from_date: fromDate,
											to_date: toDate,
											from_date_moment: val[0],
											to_date_moment: val[1],
										},
										newConfig
									);
								} else {
									const newConfig = {
										...paginateConfig,
										from_date: false,
										to_date: false,
										offset: 0,
										currentPage: 1,
									};
									setpaginateConfig(newConfig);

									// Clear from localStorage
									clearTripFilters('INTERCITY');
								}
							}}
						/>
					</Box>
					<Box
						flex="0 0 auto"
						display="flex"
						alignItems="center"
						gap="8px"
						marginLeft="auto"
					>
						<ButtonWrapper onClick={handleExportClick} icons={<DownloadOutlined />}>
							EXPORT
						</ButtonWrapper>
						{showBulkCloseUI && (
							<CloseSelectedButton
								disabled={selectedTrips.length === 0}
								onClick={() => setShowBulkCloseConfirm(true)}
							>
								CLOSE SELECTED ({selectedTrips.length})
							</CloseSelectedButton>
						)}
					</Box>
				</Flex>
			</Box>
			<Box mt={4}>
				{/* eslint-disable-next-line no-nested-ternary */}
				{isInitialLoadComplete ? (
					rowData ? (
						<TripHistoryRCTable
							rowKey="id"
							tableDatas={[rowData, setRowData]}
							togglePaginate={[paginateConfig, setpaginateConfig]}
							dispatchAction={memoizedDispatchAction}
							columns={IntercityTripTableConfig((record) => {
								setSelectedTrip(record);
								setShowEndTripModal(true);
							}, getUIVersion() === 'new')}
							rowSelection={
								showBulkCloseUI
									? {
											selectedRowKeys: selectedTrips.map((t) => t.id),
											onChange: handleBulkSelectionChange,
											preserveSelectedRowKeys: true,
											getCheckboxProps: (record) => ({
												disabled:
													eligibilityLoading ||
													!eligibilityMap[tripKey(record.id)]?.eligible,
											}),
									  }
									: undefined
							}
							scroll={{ x: true }}
						/>
					) : (
						<IntercityEmptyState>
							<EmptyStateText>No ongoing Trips</EmptyStateText>
						</IntercityEmptyState>
					)
				) : (
					<IntercityEmptyState>
						<EmptyStateText>Loading...</EmptyStateText>
					</IntercityEmptyState>
				)}
			</Box>
			<Drawer
				className="userList-drawer"
				title={<FilterTitle onClose={() => setOpen(false)} />}
				placement="right"
				closable={false}
				onClose={() => setOpen(false)}
				open={open}
			>
				<Box>
					<Box mb="2rem">
						<Text mb="5px">Business Unit</Text>
						<Select
							mode="multiple"
							showSearch
							style={{ width: '100%' }}
							name="business_unit_ids"
							value={getBusinessUnitIds()}
							onChange={(val) => {
								setpaginateConfig((prev) => ({
									...prev,
									business_unit_ids: val,
									offset: 0,
									currentPage: 1,
								}));
								if (val.length > 0) {
									message.warning(
										'Entities without an association to a Business Unit are not visible.'
									);
								}
							}}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select"
							options={businessUnitsList}
							maxTagCount="responsive"
						/>
					</Box>
					<Box mb="2rem">
						<Text mb="5px">City</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="city"
							value={paginateConfig?.city}
							onChange={(val) =>
								setpaginateConfig((prev) => ({
									...prev,
									city: val,
									project_id: undefined,
									project_location_code: undefined,
									offset: 0,
									currentPage: 1,
								}))
							}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select"
							options={citiesList}
						/>
					</Box>
					<Box mb="2rem">
						<Text mb="5px">Client</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="client_name"
							value={paginateConfig?.client_id}
							onChange={(val) => {
								// Find the selected option to get the display label
								const selectedOption = clientsList?.find(
									(option) => option.value === val
								);
								setpaginateConfig((prev) => ({
									...prev,
									client_id: val,
									client_name: selectedOption?.label || undefined,
									offset: 0,
									currentPage: 1,
								}));
							}}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select"
							options={clientsList}
						/>
					</Box>
					<Box mb="2rem">
						<Text mb="5px">Project</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="project_location_code"
							value={paginateConfig?.project_id}
							onChange={(val) => {
								// Find the selected option to get the display label
								const selectedOption = projectsList?.find(
									(option) => option.value === val
								);
								setpaginateConfig((prev) => ({
									...prev,
									project_id: val,
									project_location_code: selectedOption?.label || undefined,
									offset: 0,
									currentPage: 1,
								}));
							}}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select"
							options={projectsList}
						/>
					</Box>
					<Box mb="2rem">
						<Text mb="5px">Vendor</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="vendor_name"
							value={paginateConfig?.vendor_name}
							onChange={(val) => {
								// Find the selected option to get the display label
								const selectedOption = vendorsList?.find(
									(option) => option.value === val
								);
								setpaginateConfig((prev) => ({
									...prev,
									vendor_id: val,
									vendor_name: selectedOption?.label || undefined,
									offset: 0,
									currentPage: 1,
								}));
							}}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select"
							options={vendorsList}
						/>
					</Box>
					<Box mb="2rem">
						<Text mb="5px">Trip Type</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="trip_type"
							value="adhoc"
							placeholder="Select"
							options={[{ value: 'adhoc', label: 'Adhoc' }]}
							disabled
						/>
					</Box>
					<Box mb="2rem">
						<Text mb="5px">Trip Status</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="trip_status"
							value={paginateConfig?.trip_status}
							onChange={(val) =>
								setpaginateConfig((prev) => ({
									...prev,
									trip_status: val,
									offset: 0,
									currentPage: 1,
								}))
							}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select"
							options={TripStatusOptions}
						/>
					</Box>
					<Box mb="2rem">
						<Text mb="5px">Valid</Text>
						<Select
							showSearch
							style={{ width: '100%' }}
							name="is_valid"
							value={paginateConfig?.is_valid}
							onChange={(val) => handleValidTripTypeChange(val)}
							allowClear
							filterOption={(input, option) =>
								option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
							}
							placeholder="Select"
							options={validOptions}
						/>
					</Box>

					<Box>
						<ButtonWrapper
							marginLeft="auto"
							border="1px solid rgba(237, 108, 2, 0.5)"
							color="#ed6c02"
							onClick={() => {
								clearTripFilters('INTERCITY');
								setRangePickerValue(null);
								setpaginateConfig({
									...initialPaginateConfig,
									search_type: 'trip',
									fetch_filters: false,
									business_unit_ids: [],
									offset: 0,
									currentPage: 1,
								});
								setOpen(false);
							}}
						>
							RESET FILTERS
						</ButtonWrapper>
					</Box>
				</Box>
			</Drawer>
			{showEndTripModal &&
				canEndTrip &&
				(selectedTrip?.billing_subtype === 'Per Order' ||
				selectedTrip?.info?.billing_subtype === 'Per Order' ? (
					<EndTripModalV2
						visible={showEndTripModal}
						trip={selectedTrip}
						perOrderRates={selectedTrip?.order_rates}
						tableRefreshRef={tableRefreshRef}
						setpaginateConfig={setpaginateConfig}
						onClose={() => setShowEndTripModal(false)}
					/>
				) : (
					<EndTripModal
						visible={showEndTripModal}
						trip={selectedTrip}
						onClose={() => setShowEndTripModal(false)}
						setpaginateConfig={setpaginateConfig}
					/>
				))}
			<TripExportWarningModal
				open={exportWarningModal}
				onConfirm={handleExportWarningConfirm}
				onCancel={() => setExportWarningModal(false)}
			/>
			{showBulkCloseUI && (
				<BulkCloseTripsModal
					visible={showBulkCloseConfirm}
					selectedTrips={selectedTrips}
					loading={bulkCloseLoading}
					result={bulkCloseResult}
					onCancel={() => setShowBulkCloseConfirm(false)}
					onConfirm={handleConfirmBulkClose}
					onDone={handleDoneBulkClose}
				/>
			)}
		</>
	);
}

// {
// 	title: 'Additional Charges',
// 	dataIndex: 'additional_charges',
// 	key: 'additional_charges',
// 	width: 150,
// 	hidden: false,
// 	render: (additional_charges) => {
// 		const totalCharges = calculateTotalCharges(additional_charges);
// 		const headingStyle = { color: '#858384', fontSize: '12px' };
// 		const valueStyle = { color: 'black', fontWeight: '600', fontSize: '12px' };
// 		const totalValueStyle = { color: 'black', fontWeight: 'bold', fontSize: '12px' };
// 		const rowStyle = {
// 			display: 'flex',
// 			justifyContent: 'space-between',
// 			minWidth: '200px',
// 		};
// 		return (
// 			<Box>
// 				{additional_charges && Object.keys(additional_charges).length > 0 && (
// 					<>
// 						<div style={rowStyle}>
// 							<Text overFlow="hidden" style={headingStyle}>
// 								Halt Charges:
// 							</Text>
// 							<Text overFlow="hidden" style={valueStyle}>
// 								₹{additional_charges.haltCharges || 0}
// 							</Text>
// 						</div>
// 						<div style={rowStyle}>
// 							<Text overFlow="hidden" style={headingStyle}>
// 								Miscellaneous Charges:
// 							</Text>
// 							<Text overFlow="hidden" style={valueStyle}>
// 								₹{additional_charges.miscellaneousCharges || 0}
// 							</Text>
// 						</div>
// 						<div style={rowStyle}>
// 							<Text overFlow="hidden" style={headingStyle}>
// 								Toll Charges:
// 							</Text>
// 							<Text overFlow="hidden" style={valueStyle}>
// 								₹{additional_charges.tollCharges || 0}
// 							</Text>
// 						</div>
// 						<div style={rowStyle}>
// 							<Text overFlow="hidden" style={headingStyle}>
// 								Unloading Charges:
// 							</Text>
// 							<Text overFlow="hidden" style={valueStyle}>
// 								₹{additional_charges.unloadingCharges || 0}
// 							</Text>
// 						</div>
// 						<div style={{ ...rowStyle, marginTop: '5px' }}>
// 							<Text overFlow="hidden" style={headingStyle}>
// 								Total Charges:
// 							</Text>
// 							<Text overFlow="hidden" style={totalValueStyle}>
// 								₹{totalCharges}
// 							</Text>
// 						</div>
// 					</>
// 				)}
// 			</Box>
// 		);
// 	},
// },
